import { useCallback, useRef } from 'react';
import {
  saveDecision,
  deleteDecision,
  type Decision,
  type DecisionPayload,
} from 'src/services/DataVaultTransfer.service';

interface UseSaveDecisionOptions {
  transferId: number | null;
  onDecisionSaved: (decision: Decision) => void;
  onDecisionRemoved: (qitId: number, instanceIndex: number) => void;
  /**
   * Called when a save or delete fails. Without this the checkbox flips
   * locally while the server keeps the old decision, so the renter commits
   * documents they believe they removed. Callers should surface this inline.
   */
  onDecisionFailed?: () => void;
}

export function useSaveDecision({
  transferId,
  onDecisionSaved,
  onDecisionRemoved,
  onDecisionFailed,
}: UseSaveDecisionOptions): {
  save: (payload: DecisionPayload) => Promise<void>;
  remove: (qitId: number, instanceIndex: number) => Promise<void>;
} {
  // Keyed by `${qit_id}_${instance_index}` so that rapid toggles on the SAME
  // question cancel stale requests without interfering with other questions
  // (fixes "Discard all" batch saves aborting each other).
  const pendingRef = useRef<Map<string, AbortController>>(new Map());

  const save = useCallback(
    async (payload: DecisionPayload) => {
      if (!transferId) {
        return;
      }
      const key = `${payload.qit_id}_${payload.instance_index}`;
      // Cancel any in-flight save for the same decision only
      pendingRef.current.get(key)?.abort();
      const controller = new AbortController();
      pendingRef.current.set(key, controller);

      try {
        const { decision } = await saveDecision(transferId, payload);
        if (!controller.signal.aborted) {
          onDecisionSaved(decision);
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          // Rollback could be implemented here; for MVP, just log
          console.error('Failed to save decision:', err);
          onDecisionFailed?.();
        }
      } finally {
        // Clean up entry if this controller is still the current one for this key
        if (pendingRef.current.get(key) === controller) {
          pendingRef.current.delete(key);
        }
      }
    },
    [transferId, onDecisionSaved, onDecisionFailed]
  );

  const remove = useCallback(
    async (qitId: number, instanceIndex: number) => {
      if (!transferId) {
        return;
      }
      // Same key map as save(): a rapid re-toggle of the same question (or a
      // "Discard all" burst) must not let an older delete land after a newer
      // one and reinstate a decision the renter already reversed.
      const key = `${qitId}_${instanceIndex}`;
      pendingRef.current.get(key)?.abort();
      const controller = new AbortController();
      pendingRef.current.set(key, controller);

      try {
        await deleteDecision(transferId, qitId, instanceIndex);
        if (!controller.signal.aborted) {
          onDecisionRemoved(qitId, instanceIndex);
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error('Failed to delete decision:', err);
          onDecisionFailed?.();
        }
      } finally {
        if (pendingRef.current.get(key) === controller) {
          pendingRef.current.delete(key);
        }
      }
    },
    [transferId, onDecisionRemoved, onDecisionFailed]
  );

  return { save, remove };
}
